import React, { useEffect, useState } from "react";

import { DatePicker, Form, Input, message } from "antd";
import moment from "moment";

import LdasModal from "../LdasModal";

const newRowId = () =>
  `n-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const EMPTY_PARAM = () => ({
  rowId: newRowId(),
  parameterName: "",
  standardValue: "",
  measuredValue: "",
});

const LdasReportAddModal = ({ visible, editingReport, onCancel, onSave }) => {
  const [form] = Form.useForm();
  const [params, setParams] = useState([EMPTY_PARAM()]);
  const isEdit = Boolean(editingReport);

  useEffect(() => {
    if (!visible) return;
    if (editingReport) {
      form.setFieldsValue({
        testBody: editingReport.testBody ?? "",
        testDate: editingReport.testDate
          ? moment(editingReport.testDate, "YYYY-MM-DD")
          : undefined,
        batchNumber: editingReport.batchNumber ?? "",
      });
      const rows = (editingReport.parameters ?? []).map((p) => ({
        rowId: `s-${p.id}`,
        id: p.id,
        parameterName: p.parameterName ?? "",
        standardValue: p.standardValue ?? "",
        measuredValue: p.measuredValue ?? "",
      }));
      setParams(rows.length > 0 ? rows : [EMPTY_PARAM()]);
      return;
    }
    form.resetFields();
    setParams([EMPTY_PARAM()]);
  }, [visible, editingReport, form]);

  const handleCancel = () => {
    form.resetFields();
    setParams([EMPTY_PARAM()]);
    onCancel();
  };

  const handleParamChange = (rowId, field, value) => {
    setParams((prev) =>
      prev.map((row) =>
        row.rowId === rowId ? { ...row, [field]: value } : row,
      ),
    );
  };

  const handleParamAdd = () => {
    setParams((prev) => [...prev, EMPTY_PARAM()]);
  };

  const handleParamDelete = (rowId) => {
    setParams((prev) => prev.filter((row) => row.rowId !== rowId));
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(({ testBody, testDate, batchNumber }) => {
        const parameters = params
          .filter((row) => row.parameterName.trim())
          .map((row) => {
            const out = {
              parameterName: row.parameterName.trim(),
              standardValue: row.standardValue.trim(),
              measuredValue: row.measuredValue.trim(),
            };
            if (row.id !== undefined && row.id !== null) {
              out.id = row.id;
            }
            return out;
          });

        if (parameters.length === 0) {
          message.error(
            "테스트 파라미터를 최소 1개 이상 입력해 주세요 (파라미터명 필수)",
          );
          return;
        }

        onSave({
          ...(isEdit && editingReport?.id !== null
            ? { id: editingReport.id, result: editingReport.result }
            : {}),
          testBody: testBody.trim(),
          testDate: testDate ? testDate.format("YYYY-MM-DD") : null,
          batchNumber: batchNumber ? batchNumber.trim() : null,
          parameters,
        });
        form.resetFields();
        setParams([EMPTY_PARAM()]);
      })
      .catch(() => {});
  };

  return (
    <LdasModal
      title={isEdit ? "리포트 수정" : "리포트 추가"}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      width={900}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="testBody"
          label="테스트 기관명"
          required
          rules={[
            { required: true, message: "테스트 기관명을 입력해 주세요" },
            {
              validator: (_, value) => {
                if (value && !String(value).trim()) {
                  return Promise.reject(
                    new Error("테스트 기관명을 입력해 주세요"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="예: IFOS Testing Lab" autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="testDate"
          label="테스트 날짜"
          required
          rules={[{ required: true, message: "테스트 날짜를 선택해 주세요" }]}
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{ width: "100%" }}
            placeholder="날짜 선택"
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item
          name="batchNumber"
          label="배치 번호"
          required
          rules={[{ required: true, message: "배치 번호를 입력해 주세요" }]}
        >
          <Input placeholder="예: OP2024-001-T1" autoComplete="off" />
        </Form.Item>

        {/* 테스트 파라미터: 행은 동적이라 HTML required 대신 저장 시 검증 */}
        <Form.Item
          label="테스트 파라미터"
          required
          className="ldas-report-modal__param-section"
        >
          <div className="ldas-report-modal__param-table">
            {/* 헤더 */}
            <div className="ldas-report-modal__param-header">
              <span className="ldas-report-modal__param-col ldas-report-modal__param-col--name">
                파라미터명
              </span>
              <span className="ldas-report-modal__param-col ldas-report-modal__param-col--standard">
                기준값
              </span>
              <span className="ldas-report-modal__param-col ldas-report-modal__param-col--measured">
                측정값
              </span>
              <span className="ldas-report-modal__param-col ldas-report-modal__param-col--del" />
            </div>

            {/* 파라미터 행 목록 */}
            {params.map((row) => (
              <div key={row.rowId} className="ldas-report-modal__param-row">
                <Input
                  className="ldas-report-modal__param-col ldas-report-modal__param-col--name"
                  placeholder="예: Vitamin D3 Content"
                  value={row.parameterName}
                  onChange={(e) =>
                    handleParamChange(
                      row.rowId,
                      "parameterName",
                      e.target.value,
                    )
                  }
                />
                <Input
                  className="ldas-report-modal__param-col ldas-report-modal__param-col--standard"
                  placeholder="예: ≥4750 IU"
                  value={row.standardValue}
                  onChange={(e) =>
                    handleParamChange(
                      row.rowId,
                      "standardValue",
                      e.target.value,
                    )
                  }
                />
                <Input
                  className="ldas-report-modal__param-col ldas-report-modal__param-col--measured"
                  placeholder="예: 5100 IU"
                  value={row.measuredValue}
                  onChange={(e) =>
                    handleParamChange(
                      row.rowId,
                      "measuredValue",
                      e.target.value,
                    )
                  }
                />
                <button
                  type="button"
                  className="ldas-report-modal__param-del"
                  onClick={() => handleParamDelete(row.rowId)}
                  disabled={params.length === 1}
                >
                  ×
                </button>
              </div>
            ))}

            {/* 행 추가 버튼 */}
            <button
              type="button"
              className="ldas-report-modal__param-add"
              onClick={handleParamAdd}
            >
              + 파라미터 행 추가
            </button>
          </div>
        </Form.Item>
      </Form>
    </LdasModal>
  );
};

export default LdasReportAddModal;
